import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { captureException } from '@sentry/react';

import { ITableHeader, Table } from '../../../../components/table/Table';
import { FileUploaderButton } from '../../../document/components/FileUploaderButton';
import { getDisplayError } from '../../../../utils/get-display-error';
import { CarrierRole, linkSupplierDriverDocument, unlinkSupplierDriverDocument } from '../../models';
import { SUPPLIER_DRIVER_DOCUMENT_TYPES } from '../../driver-documents';
import { useSupplier } from './supplierContext';

const DRIVER_DOCUMENT_HEADERS: ITableHeader[] = [
  {
    id: 'driver',
    name: 'Chauffeur',
  },
  ...SUPPLIER_DRIVER_DOCUMENT_TYPES.map((docType) => ({
    id: docType.type,
    name: docType.label,
  })),
];

export const SupplierDriverDocumentsPage = () => {
  const { supplier, refreshData } = useSupplier();

  const drivers = supplier.portalUsers
    .filter((portalUser) => portalUser.role === CarrierRole.CarrierDriver)
    .slice()
    .sort((a, b) => {
      const nameA = (a.nickname ?? a.user.name).toLocaleLowerCase();
      const nameB = (b.nickname ?? b.user.name).toLocaleLowerCase();
      return nameA.localeCompare(nameB);
    });

  return (
    <div>
      <p className="text-sm text-fg-secondary mb-4">
        Verplichte documenten per chauffeur: verblijf- en werkvergunning, chauffeursattestatie, A1-document en
        IMI-verklaring. Een oranje knop betekent dat het document is opgeladen.
      </p>

      <Table
        idKey="id"
        headers={DRIVER_DOCUMENT_HEADERS}
        data={drivers}
        emptyState={
          <span className="text-sm text-fg-muted">
            Geen chauffeurs gevonden. Voeg chauffeurs toe via het tabblad{' '}
            <Link to="../users" className="link-text">
              Gebruikers Carrier Portal
            </Link>
            .
          </span>
        }
        mapData={(driver) => {
          const displayName = driver.nickname ?? driver.user.name;

          return [
            <div>
              <div>{displayName}</div>
              {driver.nickname ? <div className="text-xs text-fg-muted">{driver.user.name}</div> : null}
              {!driver.user.isActive ? <div className="text-xs text-negative-text">Inactief</div> : null}
            </div>,
            ...SUPPLIER_DRIVER_DOCUMENT_TYPES.map((docType) => {
              const file = driver.driverDocuments.find((doc) => doc.type === docType.type)?.document ?? null;

              return (
                <FileUploaderButton
                  buttonText={docType.shortLabel}
                  title={`${docType.description} — ${displayName}`}
                  initialName={`${docType.label} - ${displayName}`}
                  file={file}
                  size={8}
                  onSubmit={async (document) => {
                    await linkSupplierDriverDocument(supplier.id, driver.user.id, document.id, docType.type);
                    refreshData();
                  }}
                  unlinkDocument={
                    file
                      ? async () => {
                          try {
                            await unlinkSupplierDriverDocument(supplier.id, driver.user.id, file.id, docType.type);
                            refreshData();
                            toast.success('Document verwijderd');
                          } catch (err) {
                            captureException(err);
                            toast.error(`Kon document niet verwijderen: ${getDisplayError(err)}`);
                            throw err;
                          }
                        }
                      : undefined
                  }
                />
              );
            }),
          ];
        }}
      />
    </div>
  );
};
